import useHeader from '@hooks/useHeader';
import {
    Avatar,
    Badge,
    Box,
    Card,
    CardHeader,
    Icon,
    Link,
    Skeleton,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import { Fragment } from 'react/jsx-runtime';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '@utils/colorUtils';
import {
    useNavigate,
    useParams,
    Link as RouterLink,
    Outlet,
    useMatch,
} from '@tanstack/react-router';
import { useChildMatches } from '@tanstack/react-router';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import useDialog from '@hooks/useDialog';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { DeleteOutlineRounded, GroupAddRounded } from '@mui/icons-material';
import { contactQueryOptions } from '@utils/queryOptions';
import AddContactToGroup from '@Components/General/dialog-content/add-contact-to-group';
import { useForm } from 'react-hook-form';
import { closeOptions } from '@utils/utils';

const ConnectionDetails = ({ connections }) => {
    return (
        <Typography>
            {connections.map((connection, index) => (
                <Fragment key={index}>
                    {connection?.relationship}
                    {connection?.related_to?.display_name &&
                        connection?.relationship &&
                        ' '}
                    <Link
                        noWrap
                        component={RouterLink}
                        to={`/contacts/${connection?.related_to?.contact_id}/`}
                    >
                        {connection?.related_to?.display_name}
                    </Link>
                    {index < connections.length - 1 && ', '}
                </Fragment>
            ))}
        </Typography>
    );
};

const ContactDetails = () => {
    const { t } = useTranslation();
    const dialog = useDialog();
    const { fullPath: currentPath } = useMatch({});

    const tab = useChildMatches({})[0].pathname.split('/').pop();
    const navigate = useNavigate();

    const { control } = useForm();
    const addContactsToGroupMutation = useMutation({
        mutationKey: ['addContactsToGroup'],
        onSuccess: () => {
            dialog.close();
            closeOptions();
        },
    });

    const { contactId } = useParams({});

    const { data: contact } = useSuspenseQuery(contactQueryOptions(contactId));
    const { display_name } = contact || {};

    const headerProps = useMemo(() => {
        return {
            title: t('Contacts'),
            titleSuffix: display_name,
        };
    }, [display_name]);

    useHeader(headerProps);

    const deleteContactMutation = useMutation({
        mutationKey: ['deleteContact'],
        onSuccess: () => {
            dialog.close();
            navigate({ to: '../' });
        },
    });

    const contactOptions: QuickOption[] = [
        {
            label: t('Edit'),
            onClick: () => navigate({ to: currentPath + '/edit' }),
            Icon: props => <Icon {...props}>edit_square</Icon>,
            quickButton: true,
            variant: 'tonal',
        },
        {
            label: t('Add to Group'),
            onClick: () =>
                dialog.open({
                    title: t('Add to Group'),
                    content: <AddContactToGroup control={control} />,
                    confirmLabel: t('Add'),
                    confirmMutation: addContactsToGroupMutation,
                    onConfirm: () => ({
                        groupId: control._formValues.group.id,
                        contactIds: [contactId],
                    }),
                }),
            Icon: GroupAddRounded,
        },
        {
            label: t('Delete'),
            onClick: () =>
                dialog.open({
                    title: t('Delete Contact?'),
                    content: t('prompts:content.deleteContact'),
                    confirmMutation: deleteContactMutation,
                    confirmLabel: t('Delete'),
                    onConfirm: () => contactId,
                    confirmButtonProps: {
                        color: 'error',
                    },
                }),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
        },
    ];

    return (
        <Box>
            <Card sx={{ mb: 2 }}>
                <CardHeader
                    avatar={
                        <Avatar
                            src={contact.profile_picture}
                            sx={{
                                height: 64,
                                width: 64,
                                bgcolor: stringToColor(display_name),
                                fontSize: 32,
                            }}
                        >
                            {display_name.getNameInitials()}
                        </Avatar>
                    }
                    title={display_name}
                    // subheader={
                    //     <ConnectionDetails connections={contact.connections} />
                    // }
                    subheader={contact.description}
                    action={
                        <QuickOptions options={contactOptions} size="large" />
                    }
                />
                {/*<Tabs
                    value={tab}
                    sx={{
                        borderTop: 1,
                        borderColor: 'divider',
                        mx: -0.75,
                    }}
                >
                    <Tab
                        value=""
                        label={t('contact.Overview', { interpolation: {} })}
                        LinkComponent={RouterLink}
                        to={currentPath}
                    />

                     <Tab
                        value="donations"
                        label={
                            <Badge
                                badgeContent={4}
                                color="error"
                                variant="dot"
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                {t('Donations')}
                            </Badge>
                        }
                        LinkComponent={RouterLink}
                        to={currentPath + '/donations'}
                    />
                    <Tab
                        value="expenses"
                        label="Expenses"
                        LinkComponent={RouterLink}
                        to={currentPath + '/expenses'}
                    />
                    <Tab
                        value="files"
                        label="Files"
                        LinkComponent={RouterLink}
                        to={currentPath + '/files'}
                    />
                    <Tab
                        value="emails"
                        label="Emails"
                        LinkComponent={RouterLink}
                        to={currentPath + '/emails'}
                    /> 
                </Tabs>*/}
            </Card>
            <Outlet />
        </Box>
    );
};

export default ContactDetails;
